import { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { Search, ArrowRight, Settings, Activity, LayoutDashboard, FileText, Plus } from 'lucide-react';

interface CommandItem {
  id: string;
  label: string;
  description?: string;
  icon: React.ReactNode;
  action: () => void;
  keywords?: string[];
}

interface CommandPaletteProps {
  workspaceId?: string;
  onCreateItem?: () => void;
}

export default function CommandPalette({ workspaceId, onCreateItem }: CommandPaletteProps) {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');
  const [selectedIndex, setSelectedIndex] = useState(0);
  const inputRef = useRef<HTMLInputElement>(null);
  const navigate = useNavigate();

  const commands: CommandItem[] = [
    ...(onCreateItem ? [{
      id: 'new-item',
      label: 'New Item',
      description: 'Create a new roadmap item',
      icon: <Plus className="h-4 w-4" />,
      action: () => { onCreateItem(); setOpen(false); },
      keywords: ['create', 'add', 'new', 'item'],
    }] : []),
    {
      id: 'dashboard',
      label: 'Go to Dashboard',
      description: 'View all workspaces',
      icon: <LayoutDashboard className="h-4 w-4" />,
      action: () => { navigate('/dashboard'); setOpen(false); },
      keywords: ['home', 'workspaces', 'dashboard'],
    },
    ...(workspaceId ? [
      {
        id: 'roadmap',
        label: 'Go to Roadmap',
        description: 'View the Kanban board',
        icon: <ArrowRight className="h-4 w-4" />,
        action: () => { navigate(`/roadmap/${workspaceId}`); setOpen(false); },
        keywords: ['board', 'kanban', 'roadmap'],
      },
      {
        id: 'changelog',
        label: 'Go to Changelog',
        description: 'View changelog entries',
        icon: <FileText className="h-4 w-4" />,
        action: () => { navigate(`/changelog/${workspaceId}`); setOpen(false); },
        keywords: ['changelog', 'releases', 'updates'],
      },
      {
        id: 'activity',
        label: 'Go to Activity',
        description: 'View activity feed',
        icon: <Activity className="h-4 w-4" />,
        action: () => { navigate(`/activity/${workspaceId}`); setOpen(false); },
        keywords: ['activity', 'log', 'history'],
      },
      {
        id: 'settings',
        label: 'Go to Settings',
        description: 'Workspace settings & members',
        icon: <Settings className="h-4 w-4" />,
        action: () => { navigate(`/settings/${workspaceId}`); setOpen(false); },
        keywords: ['settings', 'members', 'config', 'preferences'],
      },
    ] : []),
  ];

  const filtered = query.trim()
    ? commands.filter(cmd => {
        const q = query.toLowerCase();
        return (
          cmd.label.toLowerCase().includes(q) ||
          cmd.description?.toLowerCase().includes(q) ||
          cmd.keywords?.some(k => k.includes(q))
        );
      })
    : commands;

  useEffect(() => {
    setSelectedIndex(0);
  }, [query]);

  const handleKeyDown = useCallback((e: KeyboardEvent) => {
    if ((e.metaKey || e.ctrlKey) && e.key === 'k') {
      e.preventDefault();
      setOpen(prev => !prev);
      setQuery('');
    }
    if (e.key === 'Escape' && open) {
      setOpen(false);
    }
  }, [open]);

  useEffect(() => {
    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [handleKeyDown]);

  useEffect(() => {
    if (open) {
      setTimeout(() => inputRef.current?.focus(), 50);
    }
  }, [open]);

  const handleItemKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setSelectedIndex(prev => Math.min(prev + 1, filtered.length - 1));
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setSelectedIndex(prev => Math.max(prev - 1, 0));
    } else if (e.key === 'Enter') {
      e.preventDefault();
      if (filtered[selectedIndex]) {
        filtered[selectedIndex].action();
      }
    }
  };

  if (!open) return null;

  return (
    <div className="fixed inset-0 z-[100] flex items-start justify-center pt-[20vh]">
      <div
        className="fixed inset-0 bg-gray-400/30 dark:bg-black/50 backdrop-blur-sm"
        onClick={() => setOpen(false)}
      />
      <div className="relative z-10 w-full max-w-lg rounded-md border border-gray-200 bg-white shadow-2xl dark:border-gray-700 dark:bg-gray-800 overflow-hidden">
        {/* Search input */}
        <div className="flex items-center gap-3 border-b border-gray-200 dark:border-gray-700 px-4 py-3">
          <Search className="h-5 w-5 text-gray-400 shrink-0" />
          <input
            ref={inputRef}
            type="text"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            onKeyDown={handleItemKeyDown}
            placeholder="Type a command or search..."
            className="flex-1 bg-transparent text-sm text-gray-900 dark:text-white placeholder:text-gray-400 outline-none"
          />
          <kbd className="hidden sm:inline-flex items-center gap-0.5 rounded border border-gray-200 bg-gray-50 px-1.5 py-0.5 text-[10px] font-medium text-gray-400 dark:border-gray-600 dark:bg-gray-700">
            ESC
          </kbd>
        </div>

        {/* Results */}
        <div className="max-h-64 overflow-y-auto py-2">
          {filtered.length === 0 ? (
            <div className="px-4 py-6 text-center text-sm text-gray-500 dark:text-gray-400">
              No results found
            </div>
          ) : (
            filtered.map((cmd, i) => (
              <button
                key={cmd.id}
                onClick={cmd.action}
                onMouseEnter={() => setSelectedIndex(i)}
                className={`flex w-full items-center gap-3 px-4 py-2.5 text-left transition-colors ${
                  i === selectedIndex
                    ? 'bg-brand-50 dark:bg-brand-950/30'
                    : 'hover:bg-gray-50 dark:hover:bg-gray-700/50'
                }`}
              >
                <div className={`flex h-8 w-8 shrink-0 items-center justify-center rounded-lg ${
                  i === selectedIndex
                    ? 'bg-brand-100 text-brand-600 dark:bg-brand-900 dark:text-brand-400'
                    : 'bg-gray-100 text-gray-500 dark:bg-gray-700 dark:text-gray-400'
                }`}>
                  {cmd.icon}
                </div>
                <div className="min-w-0 flex-1">
                  <div className="text-sm font-medium text-gray-900 dark:text-white">{cmd.label}</div>
                  {cmd.description && (
                    <div className="text-xs text-gray-500 dark:text-gray-400 truncate">{cmd.description}</div>
                  )}
                </div>
                {i === selectedIndex && (
                  <kbd className="hidden sm:inline-flex items-center rounded border border-gray-200 bg-gray-50 px-1.5 py-0.5 text-[10px] font-medium text-gray-400 dark:border-gray-600 dark:bg-gray-700">
                    Enter
                  </kbd>
                )}
              </button>
            ))
          )}
        </div>

        {/* Footer */}
        <div className="flex items-center justify-between border-t border-gray-200 dark:border-gray-700 px-4 py-2">
          <div className="flex items-center gap-3 text-[10px] text-gray-400">
            <span className="flex items-center gap-1">
              <kbd className="rounded border border-gray-200 bg-gray-50 px-1 py-0.5 dark:border-gray-600 dark:bg-gray-700">&uarr;&darr;</kbd>
              navigate
            </span>
            <span className="flex items-center gap-1">
              <kbd className="rounded border border-gray-200 bg-gray-50 px-1 py-0.5 dark:border-gray-600 dark:bg-gray-700">&crarr;</kbd>
              select
            </span>
            <span className="flex items-center gap-1">
              <kbd className="rounded border border-gray-200 bg-gray-50 px-1 py-0.5 dark:border-gray-600 dark:bg-gray-700">esc</kbd>
              close
            </span>
          </div>
        </div>
      </div>
    </div>
  );
}
